import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceEditor() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    currency: 'usd',
    line_items: [{ item: '', description: '', quantity: 1, rate: 0 }],
    tax_percent: 0,
    discount_percent: 0,
    due_date: '',
    notes: ''
  });

  async function loadData() {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        fetch(`${API_URL}/api/clients`, { credentials: 'include' }),
        fetch(`${API_URL}/api/projects`, { credentials: 'include' })
      ]);

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function addLineItem() {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { item: '', description: '', quantity: 1, rate: 0 }]
    });
  }

  function removeLineItem(index) {
    const items = [...formData.line_items];
    items.splice(index, 1);
    setFormData({ ...formData, line_items: items });
  }

  function updateLineItem(index, field, value) {
    const items = [...formData.line_items];
    items[index] = { ...items[index], [field]: value };
    setFormData({ ...formData, line_items: items });
  }

  function calculateSubtotal() {
    return formData.line_items.reduce((sum, item) => {
      return sum + (item.quantity * item.rate);
    }, 0);
  }

  function calculateTotal() {
    const subtotal = calculateSubtotal();
    const discount = subtotal * (formData.discount_percent / 100);
    const taxableAmount = subtotal - discount;
    const tax = taxableAmount * (formData.tax_percent / 100);
    return taxableAmount + tax;
  }

  async function handleSave() {
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }

    if (formData.line_items.length === 0 || !formData.line_items[0].item) {
      toast.error('Please add at least one line item');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          line_items: formData.line_items.filter(li => li.item),
          due_date: formData.due_date || undefined,
          project_id: formData.project_id || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Invoice created!');
        navigate(`/invoices/${data._id}`);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="h-64 bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="invoice-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-400" />
            New Invoice
          </h1>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-500 hover:bg-indigo-600 text-white"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving…' : 'Save Invoice'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white">Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="text-white">
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Project (optional)</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(v) => setFormData({ ...formData, project_id: v })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {projects.filter(p => !formData.client_id || p.client_id === formData.client_id).map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(v) => setFormData({ ...formData, currency: v })}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="usd" className="text-white">USD ($)</SelectItem>
                    <SelectItem value="eur" className="text-white">EUR (€)</SelectItem>
                    <SelectItem value="gbp" className="text-white">GBP (£)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium">Line Items</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addLineItem}
                  className="border-slate-600 text-white hover:bg-slate-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {formData.line_items.map((item, index) => (
                  <div key={index} className="bg-slate-900/50 rounded-lg p-4">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-4">
                        <Label className="text-slate-400 text-xs">Item</Label>
                        <Input
                          placeholder="Service name"
                          value={item.item}
                          onChange={(e) => updateLineItem(index, 'item', e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white mt-1"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-slate-400 text-xs">Description</Label>
                        <Input
                          placeholder="Optional"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className="bg-slate-800 border-slate-700 text-white mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-400 text-xs">Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="bg-slate-800 border-slate-700 text-white mt-1"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-slate-400 text-xs">Rate ($)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="bg-slate-800 border-slate-700 text-white mt-1"
                        />
                      </div>
                      <div className="col-span-1 flex items-end justify-center">
                        {formData.line_items.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-right mt-2 text-slate-400 text-sm">
                      Amount: ${(item.quantity * item.rate).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-slate-300">Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes or payment instructions..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white mt-1 min-h-[100px]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="bg-slate-800/50 border-slate-700 h-fit">
          <CardHeader>
            <CardTitle className="text-white">Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between text-slate-300">
                <span>Subtotal</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Discount (%)</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-900 border-slate-700 text-white w-20 text-right"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Tax (%)</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.tax_percent}
                  onChange={(e) => setFormData({ ...formData, tax_percent: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-900 border-slate-700 text-white w-20 text-right"
                />
              </div>
              <hr className="border-slate-700" />
              <div className="flex justify-between text-white font-bold text-lg">
                <span>Total</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
